import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { jobsService } from "../services/jobs";
import { documentsService } from "../services/documents";
import { ApiError } from "../services/api";
import { Skeleton } from "../components/ui/Skeleton";
import { Spinner } from "../components/ui/Spinner";
import { useToast } from "../context/ToastContext";
import JobSummaryBar from "../components/JobSummaryBar";
import JobDescriptionTab from "../components/tabs/JobDescriptionTab";
import JobTab from "../components/tabs/JobTab";
import MatchAnalysisTab from "../components/tabs/MatchAnalysisTab";
import DocumentTab, { type DocumentTabHandle } from "../components/tabs/DocumentTab";
import type { JobDetail, ApplicationStatus, JobPatchRequest } from "../types";

type TabId = "job" | "jd" | "analysis" | "cv" | "cl";

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { show: showToast } = useToast();

  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("jd");

  // Match Analysis streaming state
  const [analysisContent, setAnalysisContent] = useState("");
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [alignmentScore, setAlignmentScore] = useState<number | null>(null);
  const [successProb, setSuccessProb] = useState<number | null>(null);

  // Job tab state
  const [jobDirty, setJobDirty] = useState(false);
  const [jobSaving, setJobSaving] = useState(false);

  // Job Description tab edit state
  const [jdEditing, setJdEditing] = useState(false);
  const [jdSaving, setJdSaving] = useState(false);
  const [jdDraft, setJdDraft] = useState("");

  // Document tab state (dirty/generating tracked for button enable/disable)
  const [cvDirty, setCvDirty] = useState(false);
  const [cvGenerating, setCvGenerating] = useState(false);
  const [clDirty, setClDirty] = useState(false);
  const [clGenerating, setClGenerating] = useState(false);
  const [cvInHistory, setCvInHistory] = useState(false);
  const [clInHistory, setClInHistory] = useState(false);

  const cvTabRef = useRef<DocumentTabHandle>(null);
  const clTabRef = useRef<DocumentTabHandle>(null);

  const activeDocRef = activeTab === "cv" ? cvTabRef : clTabRef;
  const docDirty = activeTab === "cv" ? cvDirty : clDirty;
  const docGenerating = activeTab === "cv" ? cvGenerating : clGenerating;
  const docInHistory = activeTab === "cv" ? cvInHistory : clInHistory;

  useEffect(() => {
    if (!jobId) return;
    loadJob();
  }, [jobId]);

  async function loadJob() {
    setLoading(true);
    try {
      const data = await jobsService.get(jobId!);
      setJob(data);
      setAnalysisContent(data.match_analysis ?? "");
      setAlignmentScore(data.alignment_score);
      setSuccessProb(data.success_prob);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        navigate("/dashboard");
      } else {
        showToast("error", "Failed to load job.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(newStatus: ApplicationStatus) {
    if (!jobId) return;
    setStatusUpdating(true);
    try {
      const updated = await jobsService.update(jobId, { application_status: newStatus });
      setJob((prev) =>
        prev ? { ...prev, application_status: updated.application_status } : prev
      );
    } catch {
      showToast("error", "Failed to update status.");
    } finally {
      setStatusUpdating(false);
    }
  }

  async function handleReanalyse() {
    if (!jobId) return;
    const pre = analysisContent;
    setIsAnalysing(true);
    setAnalysisError(null);
    setAnalysisContent("");
    try {
      let acc = "";
      for await (const item of documentsService.streamAnalyse(jobId)) {
        if (typeof item === "string") {
          acc += item;
          setAnalysisContent(acc);
        } else {
          setAlignmentScore(item.scores.alignment_score);
          setSuccessProb(item.scores.success_prob);
        }
      }
    } catch (err) {
      setAnalysisContent(pre);
      setAnalysisError(err instanceof Error ? err.message : "Re-analysis failed.");
    } finally {
      setIsAnalysing(false);
    }
  }

  async function handleJobSave(patch: JobPatchRequest) {
    if (!jobId) return;
    setJobSaving(true);
    try {
      const updated = await jobsService.update(jobId, patch);
      setJob(updated);
      showToast("success", "Saved.");
    } catch {
      showToast("error", "Failed to save job.");
    } finally {
      setJobSaving(false);
    }
  }

  function handleJdEdit() {
    setJdDraft(job?.jd_content ?? "");
    setJdEditing(true);
  }

  function handleJdCancel() {
    setJdEditing(false);
  }

  async function handleJdSave() {
    if (!jobId) return;
    setJdSaving(true);
    try {
      const updated = await jobsService.update(jobId, { jd_content: jdDraft });
      setJob(updated);
      setJdEditing(false);
      showToast("success", "Saved.");
    } catch {
      showToast("error", "Failed to save job description.");
    } finally {
      setJdSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-[900px] mx-auto px-6 pt-6 pb-16 space-y-4">
        <Skeleton variant="heading" />
        <Skeleton variant="line" />
        <Skeleton variant="line" />
        <Skeleton variant="line" />
      </div>
    );
  }
  if (!job) return null;

  const analysisTabBadge =
    alignmentScore !== null && successProb !== null ? (
      <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
        ⚖️ {alignmentScore}% 🏆 {successProb}%
      </span>
    ) : null;

  const tabs: { id: TabId; label: string; badge?: React.ReactNode }[] = [
    { id: "job", label: "Job" },
    { id: "jd", label: "Job Description" },
    { id: "analysis", label: "Analysis", badge: analysisTabBadge },
    { id: "cv", label: "Resume" },
    { id: "cl", label: "Cover Letter" },
  ];

  const isDocTab = activeTab === "cv" || activeTab === "cl";

  return (
    <div className="flex flex-col h-full overflow-hidden max-w-5xl mx-auto w-full">
      <JobSummaryBar
        jobTitle={job.job_title}
        companyName={job.company_name}
        sourceUrl={job.source_url}
        status={job.application_status}
        statusUpdating={statusUpdating}
        onStatusChange={handleStatusChange}
      />

      {/* Tab navigation */}
      <div
        role="tablist"
        className="flex flex-shrink-0 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={[
                "px-4 py-3 text-sm whitespace-nowrap transition-colors duration-150 -mb-px",
                isActive
                  ? "font-bold text-slate-900 dark:text-slate-100 border-b-2 border-slate-900 dark:border-slate-100"
                  : "font-normal text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 border-b-2 border-transparent",
              ].join(" ")}
            >
              {tab.label}
              {tab.badge}
            </button>
          );
        })}
      </div>

      {/* Action buttons stripe */}
      <div
        className="flex flex-shrink-0 items-center gap-2 px-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 h-11"
        aria-label="Tab actions"
      >
        {activeTab === "job" && (
          <button
            form="job-tab-form"
            type="submit"
            disabled={!jobDirty || jobSaving}
            className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {jobSaving ? "Saving…" : "Save"}
          </button>
        )}

        {activeTab === "jd" && !jdEditing && (
          <button
            onClick={handleJdEdit}
            className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
          >
            Edit
          </button>
        )}

        {activeTab === "jd" && jdEditing && (
          <>
            <button
              onClick={handleJdSave}
              disabled={jdSaving}
              aria-label="Save"
              className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 flex items-center gap-1.5"
            >
              {jdSaving ? <Spinner size="sm" /> : "Save"}
            </button>
            <button
              onClick={handleJdCancel}
              disabled={jdSaving}
              className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Cancel
            </button>
          </>
        )}

        {activeTab === "analysis" && (
          <button
            onClick={handleReanalyse}
            disabled={isAnalysing}
            className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {isAnalysing ? "Analysing…" : analysisContent ? "Re-analyse" : "Analyse"}
          </button>
        )}

        {isDocTab && docInHistory && (
          <button
            onClick={() => activeDocRef.current?.backToEditor()}
            className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
          >
            &larr; Back to Editing
          </button>
        )}

        {isDocTab && !docInHistory && (
          <>
            <button
              onClick={() => activeDocRef.current?.generate()}
              disabled={docGenerating}
              className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Generate
            </button>
            <button
              onClick={() => activeDocRef.current?.refineClick()}
              disabled={docGenerating}
              className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Refine
            </button>
            <button
              onClick={() => activeDocRef.current?.preview()}
              disabled={docGenerating}
              className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Preview
            </button>
            <button
              onClick={() => activeDocRef.current?.save()}
              disabled={!docDirty || docGenerating}
              className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              Save
            </button>
            <button
              onClick={() => activeDocRef.current?.showHistory()}
              disabled={docGenerating}
              className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            >
              History
            </button>
          </>
        )}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        <div
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          className="h-full overflow-y-auto"
        >
          {activeTab === "job" && (
            <JobTab
              job={job}
              onDirtyChange={setJobDirty}
              saving={jobSaving}
              onSave={handleJobSave}
            />
          )}

          {activeTab === "jd" && (
            <JobDescriptionTab
              content={job.jd_content}
              isEditing={jdEditing}
              editedContent={jdDraft}
              onEditedContentChange={setJdDraft}
            />
          )}

          {activeTab === "analysis" && (
            <MatchAnalysisTab
              content={analysisContent}
              isAnalysing={isAnalysing}
              error={analysisError}
            />
          )}

          {/* Keep both DocumentTabs mounted to preserve state across tab switches */}
          <div className={activeTab === "cv" ? "h-full" : "hidden"}>
            <DocumentTab
              ref={cvTabRef}
              jobId={jobId!}
              docType="cv"
              label="Resume"
              onPreviewRequest={() => {}}
              onDirtyChange={setCvDirty}
              onGeneratingChange={setCvGenerating}
              onViewChange={(v) => setCvInHistory(v === "history")}
            />
          </div>
          <div className={activeTab === "cl" ? "h-full" : "hidden"}>
            <DocumentTab
              ref={clTabRef}
              jobId={jobId!}
              docType="cl"
              label="Cover Letter"
              onPreviewRequest={() => {}}
              onDirtyChange={setClDirty}
              onGeneratingChange={setClGenerating}
              onViewChange={(v) => setClInHistory(v === "history")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
